import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, update, get, child } from "firebase/database";
import BackgroundEffect from '../components/BackgroundEffect';

const makeEventKey = (label) =>
  label
    .toLowerCase()
    // remove Firebase forbidden chars: . # $ [ ] /
    .replace(/[.#$[\]/]/g, "")
    // collapse spaces to single space
    .replace(/\s+/g, " ")
    // trim start/end
    .trim()
    // finally remove spaces entirely
    .replace(/\s+/g, "");


const EmployeeDashboard = () => {
  const navigate = useNavigate();
  const [employeeData, setEmployeeData] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isProducing, setIsProducing] = useState(false);
  const [productionSeconds, setProductionSeconds] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedBreakType, setSelectedBreakType] = useState(null);
  const [breakType, setBreakType] = useState(null);
  const [breakSeconds, setBreakSeconds] = useState(0);
  const [breakRunning, setBreakRunning] = useState(false);
  const [showEndConfirm, setShowEndConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [otherBreakInput, setOtherBreakInput] = useState("");
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [lunchTaken, setLunchTaken] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [logoutCountdown, setLogoutCountdown] = useState(5);
  const [logoutDisabled, setLogoutDisabled] = useState(true);
  const [cumulativeBreakSeconds, setCumulativeBreakSeconds] = useState(0);
  // new state for actual production popup
  const [showActualPopup, setShowActualPopup] = useState(false);
  const [actualProductionInput, setActualProductionInput] = useState("");
  const [savingActualProduction, setSavingActualProduction] = useState(false);


  const realtimeDB = getDatabase();

  // Helper: persist break start (ms)
  const persistBreakStart = (ms = Date.now()) => {
    localStorage.setItem("breakStartTime", String(ms));
    localStorage.setItem("breakRunning", "true");
  };

  useEffect(() => {
    const handler = (e) => {
      if (showActualPopup && e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [showActualPopup]);


  // 🕒 Restore production and break states on reload
  useEffect(() => {
    // --- 🔹 Restore Production State ---
    const startTimestamp = localStorage.getItem("productionStartTime");
    const producingFlag = localStorage.getItem("isProducing");

    if (startTimestamp && producingFlag === "true") {
      const startMs = parseInt(startTimestamp, 10);
      if (!isNaN(startMs)) {
        const elapsedSeconds = Math.floor((Date.now() - startMs) / 1000);
        setProductionSeconds(Math.max(0, elapsedSeconds));
        setIsProducing(true);
        setTimerRunning(true);
      } else {
        console.warn("productionStartTime is not a number:", startTimestamp);
      }
    }

    // Restore cumulative break total
    const storedCumulative = localStorage.getItem("cumulativeBreakSeconds");
    if (storedCumulative) {
      setCumulativeBreakSeconds(parseInt(storedCumulative, 10) || 0);
    }

    // --- 🔹 Restore Break State ---
    const storedBreakStart = localStorage.getItem("breakStartTime");
    const storedBreakType = localStorage.getItem("breakType");
    const isBreakRunning = localStorage.getItem("breakRunning");

    if (storedBreakStart && storedBreakType && isBreakRunning === "true") {
      const startMs = parseInt(storedBreakStart, 10);
      if (!isNaN(startMs)) {
        const elapsed = Math.floor((Date.now() - startMs) / 1000);
        setBreakSeconds(Math.max(0, elapsed));
        setBreakRunning(true);
        setBreakType(storedBreakType);
        setSelectedBreakType(storedBreakType);
        setShowPopup(true);
      } else {
        // fallback: try legacy string parsing from DB if needed later
        console.warn("breakStartTime is not a number:", storedBreakStart);
      }
    }
  }, []);

  useEffect(() => {
    if (!showLogoutConfirm) return;

    setLogoutCountdown(5);
    setLogoutDisabled(true);

    let interval = setInterval(() => {
      setLogoutCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          setLogoutDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [showLogoutConfirm]);

  // Load employee data
  useEffect(() => {
    const JSKMID = localStorage.getItem("JSKMID");
    const Name = localStorage.getItem("Name");
    const Password = localStorage.getItem("Password");
    const id = localStorage.getItem("id");

    if (!JSKMID || !Password) {
      navigate("/login");
    } else {
      const data = { JSKMID, Name, Password, id };
      setEmployeeData(data);
      loadPreviousEvents(data);
    }
    // eslint-disable-next-line
  }, [navigate]);

  // Live clock
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Production timer — compute elapsed from persisted start timestamp every tick
  useEffect(() => {
    let intervalId = null;

    const updateProductionFromStart = () => {
      const start = localStorage.getItem("productionStartTime");
      if (!start) {
        setProductionSeconds(0);
        return;
      }
      const startMs = parseInt(start, 10);
      if (isNaN(startMs)) {
        setProductionSeconds(0);
        return;
      }
      const elapsedSeconds = Math.floor((Date.now() - startMs) / 1000);
      setProductionSeconds(Math.max(0, elapsedSeconds));
    };

    if (timerRunning) {
      // update immediately and then every second (still safe if tab was throttled)
      updateProductionFromStart();
      intervalId = setInterval(updateProductionFromStart, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [timerRunning]);

  // Break timer — compute elapsed from persisted break start timestamp every tick
  useEffect(() => {
    let intervalId = null;

    const updateBreakFromStart = () => {
      const start = localStorage.getItem("breakStartTime");
      if (!start) {
        setBreakSeconds(0);
        return;
      }
      const startMs = parseInt(start, 10);
      if (isNaN(startMs)) {
        setBreakSeconds(0);
        return;
      }
      const elapsedSeconds = Math.floor((Date.now() - startMs) / 1000);
      setBreakSeconds(Math.max(0, elapsedSeconds));
    };

    if (breakRunning) {
      updateBreakFromStart();
      intervalId = setInterval(updateBreakFromStart, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [breakRunning]);

  // Ensure timers recalc immediately when tab becomes visible / window focused
  useEffect(() => {
    const onVisibility = () => {
      if (timerRunning) {
        const start = localStorage.getItem("productionStartTime");
        const startMs = parseInt(start, 10);
        if (!isNaN(startMs)) {
          setProductionSeconds(Math.floor((Date.now() - startMs) / 1000));
        }
      }
      if (breakRunning) {
        const start = localStorage.getItem("breakStartTime");
        const startMs = parseInt(start, 10);
        if (!isNaN(startMs)) {
          setBreakSeconds(Math.floor((Date.now() - startMs) / 1000));
        }
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", onVisibility);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", onVisibility);
    };
  }, [timerRunning, breakRunning]);

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Load previous events + record daily login time once
  const loadPreviousEvents = useCallback(async (emp) => {
    try {
      const now = new Date();
      const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, "_");
      const formattedTime = now.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
        hour12: true,
      });

      const dbRef = ref(realtimeDB);
      const snapshot = await get(child(dbRef, `${formattedDate}/${emp.JSKMID}`));

      if (!snapshot.exists() || !snapshot.val().LoginTime) {
        const userRef = ref(realtimeDB, `${formattedDate}/${emp.JSKMID}`);
        await update(userRef, {
          LoginTime: formattedTime,
          Name: emp.Name || "",
        });
      }

      const freshSnapshot = await get(child(dbRef, `${formattedDate}/${emp.JSKMID}`));

      if (freshSnapshot.exists()) {
        const data = freshSnapshot.val();

        const hasLunchBreak = Object.keys(data).some((key) =>
          key.toLowerCase().includes("lunchbreak")
        );
        setLunchTaken(hasLunchBreak);

        const formatted = Object.entries(data)
          .map(([key, value]) => {
            const displayKey = key
              .replace(/\d+$/, (num) => ` ${num}`)
              .replace(/([a-z])([A-Z])/g, "$1 $2")
              .replace(/([a-z])([0-9])/gi, "$1 $2")
              .replace(/([A-Z])/g, (m) => ` ${m}`)
              .trim()
              .replace(/\s+/g, " ")
              .replace(/break/gi, "Break")
              .replace(/production/gi, "Production")
              .replace(/doubtlead/gi, "Doubt: Lead")
              .replace(/doubtpm/gi, "Doubt: PM")
              .replace(/\b(\w)/g, (s) => s.toUpperCase());

            let displayValue = value;

            if (typeof value === "string" && value.includes("-")) {
              const [start, end, duration] = value.split("-");
              displayValue = `${start} → ${end} (${duration})`;
            }

            return { type: displayKey, timestamp: displayValue };
          })
          .sort((a, b) => {
            const extractTime = (t) => {
              const [time] = t.timestamp.split("→");
              const trimmed = (time || t.timestamp || "").trim();
              const parsed = new Date(`1970/01/01 ${trimmed}`).getTime();
              return isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
            };
            return extractTime(a) - extractTime(b);
          });

        setEvents(formatted);
        try {
          const hasActual = !!data.ActualProduction;
          const pendingFlag = !!data.ActualProductionPending;
          // productionstarted includes '-' when it is end-start-duration. If there is an end AND actual production missing -> force popup
          const productionEnded = typeof data.productionstarted === "string" && data.productionstarted.includes("-");
          if (productionEnded && (!hasActual || pendingFlag)) {
            // open the persistent popup; do not allow it to be closed except by submit
            setActualProductionInput(data.ActualProduction || "");
            setShowActualPopup(true);
            // Also store a local marker so other code can know it's required on this client if needed
            localStorage.setItem("actualProductionPendingLocal", "true");
          } else {
            localStorage.removeItem("actualProductionPendingLocal");
          }
        } catch (e) {
          console.warn("Could not evaluate ActualProduction pending state", e);
        }
      } else {
        setEvents([]);
      }
    } catch (error) {
      console.error("Error loading events:", error);
    }
  }, [realtimeDB]);

  // --- NEW: ensure ActualProduction popup opens on refresh if DB says pending or missing actual ---
  useEffect(() => {
    if (!employeeData) return;
    let mounted = true;

    const checkActualPending = async () => {
      try {
        const now = new Date();
        const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, "_");
        const empId = employeeData.JSKMID;
        const dbRef = ref(realtimeDB);
        console.log("checkActualPending -> reading:", `${formattedDate}/${empId}`);
        const snap = await get(child(dbRef, `${formattedDate}/${empId}`));
        console.log("checkActualPending snap.exists():", snap.exists());
        if (!snap.exists()) {
          // nothing for today — nothing to do
          return;
        }
        const data = snap.val();
        console.log("checkActualPending data:", {
          productionstarted: data.productionstarted,
          ActualProduction: data.ActualProduction,
          ActualProductionPending: data.ActualProductionPending,
        });

        const parts = typeof data.productionstarted === "string" ? data.productionstarted.split("-").filter(Boolean) : [];
        const productionEnded = parts.length >= 3;
        const hasActual = !!data.ActualProduction;
        const pendingFlag = data.HasOwnProperty ? !!data.ActualProductionPending : !!data.ActualProductionPending;

        if (productionEnded && (!hasActual || pendingFlag)) {
          if (!mounted) return;
          setActualProductionInput(data.ActualProduction || "");
          setShowActualPopup(true);
          localStorage.setItem("actualProductionPendingLocal", "true");
        } else {
          localStorage.removeItem("actualProductionPendingLocal");
        }
      } catch (err) {
        console.error("checkActualPending error:", err);
      }
    };

    checkActualPending();

    return () => {
      mounted = false;
    };
  }, [employeeData, realtimeDB]);

  // Save event in DB
  const saveEvent = async (type) => {
    if (!employeeData) return;

    const now = new Date();
    const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, "_");
    const formattedTime = now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const empId = employeeData.JSKMID;
    const eventKey = type.toLowerCase().replace(/\s+/g, "");

    setIsSaving(true);
    try {
      const dbRef = ref(realtimeDB, `${formattedDate}/${empId}`);
      await update(dbRef, { [eventKey]: formattedTime });
      await loadPreviousEvents(employeeData); // 🔄 instantly reload after saving
    } catch (error) {
      console.error("❌ Error saving event:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Save logout time to DB for current user (same format as other events)
  const saveLogoutTime = async () => {
    // try to get emp id from state first, fallback to localStorage if needed
    const emp = employeeData?.JSKMID || localStorage.getItem("JSKMID");
    if (!emp) {
      console.warn("saveLogoutTime: no employee id available");
      return;
    }

    const now = new Date();
    const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, "_");
    const formattedTime = now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    try {
      const dbRef = ref(realtimeDB, `${formattedDate}/${emp}`);
      await update(dbRef, { LogoutTime: formattedTime });
      console.log("Logout time saved:", formattedTime);
    } catch (err) {
      console.error("Error saving logout time:", err);
      // you may want to retry or queue for later
    }
  };


  // ▶️ Handle Start Production (after a break)
  const handleStartProduction = async () => {
    if (!employeeData) return;

    const now = new Date();
    const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, "_");
    const formattedTime = now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const empId = employeeData.JSKMID;
    const dbRef = ref(realtimeDB, `${formattedDate}/${empId}`);

    try {
      // --- 1) If we are returning from a break, close the break entry first ---
      if (breakRunning && breakType) {
        setBreakRunning(false);

        // Find last break key of this type in DB
        const snapshot = await get(dbRef);
        if (snapshot.exists()) {
          const data = snapshot.val();
          const eventKey = makeEventKey(breakType);
          const matchingKeys = Object.keys(data).filter((k) => k.startsWith(eventKey));
          const lastBreakKey = matchingKeys[matchingKeys.length - 1];
          const lastBreakValue = lastBreakKey ? data[lastBreakKey] : null;

          // Prefer authoritative ms start from localStorage
          const storedBreakStartMs = parseInt(localStorage.getItem("breakStartTime"), 10);
          let diffSec = 0;
          let startHuman = null;

          if (!isNaN(storedBreakStartMs)) {
            // we have reliable millisecond start
            const endMs = Date.now();
            diffSec = Math.max(0, Math.floor((endMs - storedBreakStartMs) / 1000));
            const startDate = new Date(storedBreakStartMs);
            startHuman = startDate.toLocaleTimeString("en-US", {
              hour: "numeric",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            });
          } else if (lastBreakValue && typeof lastBreakValue === "string" && !lastBreakValue.includes("-")) {
            // fallback: parse the DB human time string (legacy)
            const [startTimeStr] = lastBreakValue.split("-"); // lastBreakValue likely just the human time
            const startDate = new Date(`1970/01/01 ${startTimeStr}`);
            const endDate = new Date(`1970/01/01 ${formattedTime}`);
            const diffMsFallback = endDate - startDate;
            diffSec = Math.max(0, Math.floor(diffMsFallback / 1000));
            startHuman = startTimeStr;
          }

          if (lastBreakKey && startHuman !== null) {
            const hrs = Math.floor(diffSec / 3600);
            const mins = Math.floor((diffSec % 3600) / 60);
            const secs = diffSec % 60;
            const duration = `${hrs.toString().padStart(2, "0")}:${mins
              .toString()
              .padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;

            // accumulate into cumulativeBreakSeconds
            const prev = parseInt(localStorage.getItem("cumulativeBreakSeconds") || "0", 10) || 0;
            const updated = prev + diffSec;
            localStorage.setItem("cumulativeBreakSeconds", String(updated));
            setCumulativeBreakSeconds(updated);

            // Save human-readable start-end-duration into DB (keeps your DB visible format)
            await update(dbRef, {
              [lastBreakKey]: `${startHuman}-${formattedTime}-${duration}`,
            });
            await loadPreviousEvents(employeeData);
          }
        }

        // clear persisted break state
        localStorage.removeItem("breakStartTime"); // safe to remove; we've used it
        localStorage.removeItem("breakType");
        localStorage.removeItem("breakRunning");

        setBreakType(null);
        setBreakSeconds(0);
      }

      // compute and persist start timestamp (ms). If resuming, set start = now - productionSeconds*1000
      const nowMs = Date.now();
      const persistedStartMs =
        productionSeconds && productionSeconds > 0
          ? Math.floor(nowMs - productionSeconds * 1000)
          : nowMs;

      localStorage.setItem("productionStartTime", String(persistedStartMs));
      localStorage.setItem("isProducing", "true");

      // Ensure state reflects active production and timer
      setIsProducing(true);
      setTimerRunning(true);
      setProductionSeconds(Math.floor((Date.now() - persistedStartMs) / 1000));

      const snapshot = await get(dbRef);
      const data = snapshot.exists() ? snapshot.val() : {};
      if (!data.productionstarted) {
        await saveEvent("Production Started");
      }

      // reload events so UI updates
      await loadPreviousEvents(employeeData);
    } catch (err) {
      console.error("❌ Error starting/resuming production:", err);
    }
  };

  // Stop Production → show popup
  const handleStopProduction = () => {
    setShowPopup(true);
  };

  const handleEndProduction = useCallback(async () => {
    if (!employeeData) return;

    const now = new Date();
    const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, "_");
    const formattedTime = now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
    const empId = employeeData.JSKMID;
    const dbRef = ref(realtimeDB, `${formattedDate}/${empId}`);

    try {
      const snapshot = await get(dbRef);
      if (snapshot.exists()) {
        const data = snapshot.val();
        const startValue = data.productionstarted;

        if (startValue && !startValue.includes("-")) {
          const startTimeStr = startValue;

          const cumulative = parseInt(localStorage.getItem("cumulativeBreakSeconds") || "0", 10) || 0;
          const formattedBreakTotal = formatTime(cumulative);

          // Save start, end and duration (same as before)
          await update(dbRef, {
            productionstarted: `${startTimeStr}-${formattedTime}-${formatTime(productionSeconds)}`,
            breakstotal: formattedBreakTotal,
            ActualProductionPending: true,
          });

          // Stop timer + clear persistence
          setTimerRunning(false);
          setIsProducing(false);
          localStorage.removeItem("productionStartTime");
          localStorage.removeItem("isProducing");
          localStorage.removeItem("cumulativeBreakSeconds");
          setCumulativeBreakSeconds(0);

          // reload UI events
          await loadPreviousEvents(employeeData);
          const endedHour = now.getHours();
          if (endedHour >= 9) {
            // open popup so user can input actual production hours
            setActualProductionInput(""); // clear any previous input
            setShowActualPopup(true);
          }
        } else {
          alert("Production already ended or not started yet.");
        }
      }
    } catch (err) {
      console.error("❌ Error ending production:", err);
    }
  }, [employeeData, realtimeDB, productionSeconds, loadPreviousEvents]);

  // Auto end production at 7:00 PM (19:00) local time — robust and persistent
  useEffect(() => {
    const AUTO_END_KEY = "autoEndTriggeredDate";   // set after successful end
    const AUTO_END_PENDING = "autoEndPendingDate"; // set while attempts are ongoing

    const TARGET_HOUR_24 = 19;
    const TARGET_MINUTE = 0;

    const todayKeyFor = (d = new Date()) => d.toISOString().slice(0, 10);

    const isAlreadyTriggeredToday = () =>
      localStorage.getItem(AUTO_END_KEY) === todayKeyFor();

    const markTriggeredToday = () =>
      localStorage.setItem(AUTO_END_KEY, todayKeyFor());

    const markPendingToday = () =>
      localStorage.setItem(AUTO_END_PENDING, todayKeyFor());

    const clearPending = () => localStorage.removeItem(AUTO_END_PENDING);

    const isPendingToday = () =>
      localStorage.getItem(AUTO_END_PENDING) === todayKeyFor();

    // compute ms until the next 19:00 local (today or tomorrow)
    const msUntilNextTarget = () => {
      const now = new Date();
      const target = new Date(now);
      target.setHours(TARGET_HOUR_24, TARGET_MINUTE, 0, 0);
      if (target.getTime() <= now.getTime()) {
        target.setDate(target.getDate() + 1);
      }
      return Math.max(0, target.getTime() - now.getTime());
    };

    // If clock is already past today's target and not yet triggered, return true
    const isPastTodayTargetAndNotTriggered = (date = new Date()) => {
      const target = new Date(date);
      target.setHours(TARGET_HOUR_24, TARGET_MINUTE, 0, 0);
      return date.getTime() >= target.getTime() && !isAlreadyTriggeredToday();
    };

    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    const tryAutoEndUntilSuccess = async () => {
      if (isAlreadyTriggeredToday()) {
        clearPending();
        return;
      }

      markPendingToday();

      let attempt = 0;
      let delayMs = 1000;
      const maxDelayMs = 60000;
      const maxAttempts = 1000;

      while (!isAlreadyTriggeredToday() && attempt < maxAttempts) {
        attempt++;

        try {
          if (!employeeData) {
            console.log("Auto end: employeeData not ready; retrying...");
            await sleep(Math.min(5000, delayMs));
            delayMs = Math.min(maxDelayMs, Math.floor(delayMs * 1.3));
            continue;
          }

          if (!(timerRunning || isProducing)) {
            console.log("Auto end: production not active; retry later...");
            await sleep(Math.min(30000, delayMs));
            delayMs = Math.min(maxDelayMs, Math.floor(delayMs * 1.3));
            continue;
          }

          console.log(`Auto end: attempt ${attempt}...`);
          await handleEndProduction();

          // SUCCESS
          markTriggeredToday();
          clearPending();
          console.log("Auto end: success");
          return;

        } catch (err) {
          console.error(`Auto end attempt ${attempt} failed`, err);
          await sleep(delayMs);
          delayMs = Math.min(maxDelayMs, Math.floor(delayMs * 1.5));
        }
      }

      console.warn("Auto end: retries exhausted (pending continues for next cycle)");
    };

    // Primary check function (called from interval, focus/visibility handlers, mount)
    const checkAndTrigger = async () => {
      // if already succeeded today -> nothing to do
      if (isAlreadyTriggeredToday()) {
        clearPending();
        return;
      }

      const now = new Date();

      // If it's exactly the target minute OR we've passed the target and not yet triggered
      // OR we have a pending flag (previous failed attempts), start the relentless attempts.
      const isExactMinute = now.getHours() === TARGET_HOUR_24 && now.getMinutes() === TARGET_MINUTE;
      if (isExactMinute || isPendingToday() || isPastTodayTargetAndNotTriggered(now)) {
        // start retry flow but don't await it here (fire-and-forget)
        tryAutoEndUntilSuccess();
      }
    };

    // Schedule an exact timeout to the next 19:00 (fires once)
    let exactTimeoutId = null;
    const scheduleExactTimeout = () => {
      if (exactTimeoutId) {
        clearTimeout(exactTimeoutId);
        exactTimeoutId = null;
      }
      const ms = msUntilNextTarget();
      exactTimeoutId = setTimeout(async () => {
        // At the exact trigger, mark pending and start attempts immediately
        markPendingToday();
        tryAutoEndUntilSuccess().catch((err) => console.error("Auto end background error:", err));
        // schedule next day's timeout
        scheduleExactTimeout();
      }, ms);
      console.log(`Auto end: exact scheduled in ${Math.round(ms / 1000)}s`);
    };

    // Polling fallback (cheap)
    const POLL_MS = 15 * 1000;
    const intervalId = setInterval(checkAndTrigger, POLL_MS);

    // Visibility/focus/online handlers recover from throttling / sleep / network
    const handleActive = () => {
      console.log("Auto end: active event (visibility/focus/online) - re-checking");
      checkAndTrigger();
      // reschedule exact (in case timers were delayed)
      scheduleExactTimeout();
    };

    // --- IMPORTANT: use named handlers so cleanup can remove them ---
    const visibilityHandler = () => {
      if (document.visibilityState === "visible") {
        handleActive();
      }
    };

    document.addEventListener("visibilitychange", visibilityHandler);
    window.addEventListener("focus", handleActive);
    window.addEventListener("online", handleActive);

    // NEW (Safari support)
    window.addEventListener("pagehide", handleActive);
    window.addEventListener("pageshow", handleActive);

    // Run an immediate check on mount
    checkAndTrigger();
    scheduleExactTimeout();

    // If pending left from a previous session, start attempts right away
    if (isPendingToday()) {
      console.log("Auto end: pending detected on mount, starting retry attempts");
      tryAutoEndUntilSuccess().catch((err) => console.error("Auto end background error:", err));
    }

    // Cleanup
    return () => {
      clearInterval(intervalId);
      if (exactTimeoutId) clearTimeout(exactTimeoutId);

      document.removeEventListener("visibilitychange", visibilityHandler);
      window.removeEventListener("focus", handleActive);
      window.removeEventListener("online", handleActive);

      // NEW (Safari support)
      window.removeEventListener("pagehide", handleActive);
      window.removeEventListener("pageshow", handleActive);
    };
    // keep same deps as your current effect so it re-initializes when these change
  }, [timerRunning, isProducing, handleEndProduction, employeeData]);


  const handleConfirmEndProduction = () => {
    setShowEndConfirm(true);
  };

  const submitActualProduction = async () => {
    if (!employeeData) return;
    const value = (actualProductionInput || "").toString().trim();
    if (!value) {
      alert("Please enter the actual production (positive number or text as required).");
      return;
    }

    setSavingActualProduction(true);
    try {
      const now = new Date();
      const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, "_");
      const empId = employeeData.JSKMID;
      const dbRef = ref(realtimeDB, `${formattedDate}/${empId}`);

      // Save the exact string (or number) and clear the pending marker
      await update(dbRef, {
        ActualProduction: value,
        ActualProductionPending: null, // remove pending flag from RTDB
      });

      // refresh UI events
      await loadPreviousEvents(employeeData);

      // remove local pending marker and close popup
      localStorage.removeItem("actualProductionPendingLocal");
      localStorage.removeItem("breakStartTime");
      localStorage.removeItem("breakType");
      localStorage.removeItem("breakRunning");
      localStorage.removeItem("productionStartTime");
      localStorage.removeItem("isProducing");
      localStorage.removeItem("cumulativeBreakSeconds");
      setShowActualPopup(false);
      setActualProductionInput("");
    } catch (err) {
      console.error("Error saving Actual Production:", err);
      alert("Failed to save — please try again.");
    } finally {
      setSavingActualProduction(false);
    }
  };


  const confirmEndProduction = async () => {
    await handleEndProduction();
    setShowEndConfirm(false);
  };

  const cancelEndProduction = () => {
    setShowEndConfirm(false);
  };

  // ⏱ Handle Break Start
  const handleBreakSelect = async (selectedBreak) => {
    if (!selectedBreak) return;

    if (selectedBreak === "Others") {
      setShowOtherInput(true);
      return;
    }

    if (selectedBreak === "Lunch Break") setLunchTaken(true);

    setSelectedBreakType(selectedBreak);
    setTimerRunning(false);
    setIsProducing(false);
    setBreakType(selectedBreak);
    setBreakSeconds(0);
    setBreakRunning(true);

    const now = new Date();
    const formattedTime = now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, "_");
    const empId = employeeData.JSKMID;
    const eventKey = makeEventKey(selectedBreak);

    const dbRef = ref(realtimeDB, `${formattedDate}/${empId}`);
    const snapshot = await get(dbRef);
    let breakCount = 1;
    if (snapshot.exists()) {
      const data = snapshot.val();
      const matchingKeys = Object.keys(data).filter((k) => k.startsWith(eventKey));
      breakCount = matchingKeys.length + 1;
    }

    const breakKey = `${eventKey}${breakCount}`;
    await update(dbRef, { [breakKey]: formattedTime });
    await loadPreviousEvents(employeeData);

    // ✅ Persist break info (ms)
    persistBreakStart();
    localStorage.setItem("breakType", selectedBreak);
    setSelectedBreakType(selectedBreak);
  };

  const handleConfirmOtherBreak = async () => {
    if (!otherBreakInput.trim()) {
      alert("Please enter a reason for 'Others' break.");
      return;
    }

    const breakName = otherBreakInput.trim();
    setShowOtherInput(false);
    setTimerRunning(false);
    setIsProducing(false);
    setBreakType(breakName);
    setBreakSeconds(0);
    setBreakRunning(true);

    const now = new Date();
    const formattedTime = now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });

    const formattedDate = now.toLocaleDateString("en-GB").replace(/\//g, "_");
    const empId = employeeData.JSKMID;
    const eventKey = makeEventKey(breakName);

    const dbRef = ref(realtimeDB, `${formattedDate}/${empId}`);
    const snapshot = await get(dbRef);
    let breakCount = 1;
    if (snapshot.exists()) {
      const data = snapshot.val();
      const matchingKeys = Object.keys(data).filter((k) => k.startsWith(eventKey));
      breakCount = matchingKeys.length + 1;
    }

    const breakKey = `${eventKey}${breakCount}`;
    await update(dbRef, { [breakKey]: formattedTime });
    await loadPreviousEvents(employeeData);

    // ✅ Persist custom break (ms)
    persistBreakStart();
    localStorage.setItem("breakType", breakName);
    setSelectedBreakType(breakName);

    setOtherBreakInput("");
  };

  const handleLogout = async () => {
    try {
      await saveLogoutTime();
      localStorage.clear();
      navigate("/login");
      window.location.reload();
    } catch (err) {
      console.error("Error during handleLogout:", err);
    }
  };

  const handleConfirmLogout = async () => {
    try {
      if (timerRunning || isProducing) {
        await confirmEndProduction();
      } else if (breakRunning && breakType) {
        // optionally: close break and end production here
        await handleStartProduction();    // this will close the break and write duration
        await handleEndProduction();      // then end production
      }
      await handleLogout();
    } catch (err) {
      console.error("Error during logout process:", err);
    }
  };

  const formatDate = (date) =>
    date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const handleRequestStartProduction = () => {
    setPasswordInput("");
    setPasswordError("");
    setShowPasswordConfirm(true);
  };

  const cancelPasswordConfirm = () => {
    setShowPasswordConfirm(false);
    setPasswordInput("");
    setPasswordError("");
  };

  const confirmPasswordAndStartProduction = async () => {
    const storedPassword = localStorage.getItem("Password") || "";
    if (passwordInput === storedPassword) {
      // correct password -> perform original start production logic
      setShowPasswordConfirm(false);
      setShowPopup(false);            // close break popup
      await handleStartProduction();  // your existing function
      setSelectedBreakType(null);
      setPasswordInput("");
      setPasswordError("");
    } else {
      setPasswordError("Incorrect password. Please try again.");
    }
  };

  const formatClock = (date) => date.toLocaleTimeString("en-US", { hour12: true });

  return (
    <div style={styles.page}>
      <BackgroundEffect />
      {employeeData ? (
        <div style={styles.dashboard}>
          <div style={styles.leftCard}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px" }}>
              <div>
                <h2 style={styles.header}>
                  Hello, {employeeData.Name} ({employeeData.JSKMID})
                </h2>
                <p style={styles.date}>{formatDate(currentTime)}</p>
              </div>
              <button style={styles.logoutBtn} onClick={() => setShowLogoutConfirm(true)}>
                Logout
              </button>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px" }}>
              <h1 style={styles.clock}>{formatClock(currentTime)}</h1>

              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <h2 style={styles.productionWrapper}>
                  <span style={styles.productionLabel}>Production:</span>
                  <span style={styles.clock}>{formatTime(productionSeconds)}</span>
                </h2>
                <h2 style={styles.productionWrapper}>
                  <span style={styles.productionLabel}>Total Break:</span>
                  <span style={styles.clock}>{formatTime(
                    cumulativeBreakSeconds + (breakRunning ? breakSeconds : 0)
                  )}</span>
                </h2>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between" }}>
              {!isProducing ? (
                <button style={styles.startBtn} onClick={handleStartProduction}>
                  Start Production
                </button>
              ) : (
                <button style={styles.stopBtn} onClick={handleStopProduction}>
                  Stop Production
                </button>
              )}
              <div style={{ marginTop: "20px" }}>
                <button style={styles.endBtn} onClick={handleConfirmEndProduction}>
                  End Production
                </button>
              </div>
            </div>

            {showPopup && (
              <div style={styles.popupOverlay}>
                <div style={styles.popupContainer}>
                  {!selectedBreakType ? (
                    <>
                      <h2 style={{ color: "#374651", marginBottom: "15px" }}>Select Break Type</h2>
                      <div style={styles.breakList}>
                        {["Lunch Break", "Rest Room", "Doubt Lead", "Doubt PM", "Others"].map((item) => (
                          <div key={item} style={{ display: "flex", flexDirection: "column" }}>
                            <button
                              style={{
                                ...styles.breakButton,
                                backgroundColor:
                                  item === "Lunch Break" && lunchTaken ? "#ccc" : "#e87115",
                                cursor:
                                  item === "Lunch Break" && lunchTaken ? "not-allowed" : "pointer",
                              }}
                              onClick={() => {
                                if (item === "Lunch Break" && lunchTaken) return; // 🚫 disable click
                                handleBreakSelect(item);
                              }}
                              disabled={item === "Lunch Break" && lunchTaken}
                            >
                              {item}
                            </button>

                            {/* Show input if Others is selected */}
                            {item === "Others" && showOtherInput && (
                              <div style={{ marginTop: "10px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                <input
                                  type="text"
                                  placeholder="Enter break reason"
                                  value={otherBreakInput}
                                  onChange={(e) => setOtherBreakInput(e.target.value)}
                                  style={{
                                    padding: "12px 15px",
                                    borderRadius: "8px",
                                    border: "1px solid #e87115",
                                    fontSize: "15px",
                                    color: "#333",
                                  }}
                                />
                                <button
                                  onClick={handleConfirmOtherBreak}
                                  style={{
                                    backgroundColor: "#007bff",
                                    color: "#fff",
                                    border: "none",
                                    padding: "8px 15px",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                  }}
                                >
                                  Confirm
                                </button>
                                {/* ❌ Cancel button */}
                                <button
                                  onClick={() => {
                                    setShowOtherInput(false);
                                    setOtherBreakInput("");
                                  }}
                                  style={{
                                    backgroundColor: "#dc3545",
                                    color: "#fff",
                                    border: "none",
                                    padding: "8px 12px",
                                    borderRadius: "8px",
                                    cursor: "pointer",
                                  }}
                                >
                                  ✖
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>

                      <button
                        style={{
                          ...styles.confirmBtn,
                          backgroundColor: "#6c757d",
                        }}
                        onClick={() => setShowPopup(false)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <h2 style={{ color: "#374651" }}>{selectedBreakType}</h2>
                      <h3 style={{ color: "#ff4747", fontSize: "32px", margin: "10px 0" }}>
                        {formatTime(breakSeconds)}
                      </h3>

                      {/* If password confirmation requested, show small inline form */}
                      {showPasswordConfirm ? (
                        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", alignItems: "center" }}>
                          {/* 🔹 Password input first */}
                          <div style={styles.passwordWrapper}>
                            <input
                              type={showPassword ? "text" : "password"}
                              placeholder="Enter password to confirm"
                              value={passwordInput}
                              onChange={(e) => {
                                setPasswordInput(e.target.value);
                                if (passwordError) setPasswordError("");
                              }}
                              style={{
                                padding: "10px 12px",
                                borderRadius: "8px",
                                border: "1px solid #e0e0e0",
                                fontSize: "15px",
                                width: "250px",
                                textAlign: "center",
                                paddingRight: "44px"
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => setShowPassword((s) => !s)}
                              aria-label={showPassword ? "Hide password" : "Show password"}
                              style={styles.toggleButton}
                            >
                              {showPassword ? "🙈" : "👁️"}
                            </button>
                          </div>
                          {/* 🔹 Buttons below input */}
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "center",
                              gap: "10px",
                              marginTop: "12px",
                            }}
                          >
                            <button
                              style={{ ...styles.confirmBtn, backgroundColor: "#28a745" }}
                              onClick={confirmPasswordAndStartProduction}
                            >
                              Confirm
                            </button>
                            <button
                              style={{ ...styles.confirmBtn, backgroundColor: "#6c757d" }}
                              onClick={cancelPasswordConfirm}
                            >
                              Cancel
                            </button>
                          </div>

                          {/* 🔹 Error message below buttons */}
                          {passwordError && (
                            <div style={{ color: "#d9534f", marginTop: 10, fontSize: "14px" }}>
                              {passwordError}
                            </div>
                          )}
                        </div>

                      ) : (
                        <button
                          style={styles.startBtn}
                          onClick={() => {
                            // open password confirmation before starting production
                            handleRequestStartProduction();
                          }}
                        >
                          Start Production
                        </button>
                      )}
                    </>

                  )}
                </div>
              </div>
            )}

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
              <div style={styles.overlay}>
                <div style={styles.modal}>
                  <h3>Confirm Logout</h3>
                  <p style={{ fontSize: "20px", marginTop: "10px" }}>
                    Are you sure you want to log out?
                  </p>

                  <div style={styles.buttonContainer}>
                    <button
                      style={{
                        ...styles.confirmBtn,
                        backgroundColor: logoutDisabled ? "#aaa" : "#d9534f",
                        cursor: logoutDisabled ? "not-allowed" : "pointer",
                        opacity: logoutDisabled ? 0.7 : 1,
                      }}
                      disabled={logoutDisabled}
                      onClick={handleConfirmLogout}
                    >
                      {logoutDisabled
                        ? `Yes, Logout (${logoutCountdown})`
                        : "Yes, Logout"}
                    </button>

                    <button
                      style={{
                        ...styles.confirmBtn,
                        backgroundColor: "#6c757d",
                      }}
                      onClick={() => {
                        setShowLogoutConfirm(false);
                        setLogoutCountdown(5);
                        setLogoutDisabled(true);
                      }}

                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}


            {/*End Production Confirmation Modal */}
            {showEndConfirm && (
              <div style={styles.popupOverlay}>
                <div style={styles.confirmPopup}>
                  <h3>Confirm End Production</h3>
                  <p style={{ fontSize: "20px", margin: "10px" }}>Are you sure you want to end production?</p>
                  <div style={{ display: "flex", justifyContent: "center", gap: "20px" }}>
                    <button style={{
                      ...styles.confirmBtn,
                      backgroundColor: "#d9534f",
                    }}
                      onClick={confirmEndProduction}>
                      Confirm
                    </button>
                    <button style={{
                      ...styles.confirmBtn,
                      backgroundColor: "#6c757d",
                    }}
                      onClick={cancelEndProduction}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Actual Production input popup (shown when production ended at/after 7pm) */}
            {showActualPopup && (
              <div style={styles.popupOverlay} /* no onClick handler - clicking overlay won't close */>
                <div style={styles.popupContainer} onClick={(e) => e.stopPropagation()}>
                  <h2 style={{ color: "#374651", marginBottom: "12px" }}>
                    Please enter the Actual Production Hours<br /> in the input below
                  </h2>
                  <input
                    type="number"
                    placeholder="E.g. 8.123"
                    value={actualProductionInput}
                    onChange={(e) => setActualProductionInput(e.target.value)}
                    step="0.001"
                    min="0"
                    style={{
                      padding: "12px 15px",
                      borderRadius: "8px",
                      border: "1px solid #e0e0e0",
                      fontSize: "16px",
                      width: "50%",
                      margin: "16px",
                    }}
                  />

                  <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
                    <button
                      onClick={submitActualProduction}
                      disabled={savingActualProduction}
                      style={{
                        ...styles.confirmBtn,
                        backgroundColor: "#28a745",
                        opacity: savingActualProduction ? 0.7 : 1,
                      }}
                    >
                      {savingActualProduction ? "Saving..." : "Submit"}
                    </button>
                  </div>
                </div>
              </div>
            )}


            <div style={styles.historySection}>
              <h3 style={styles.summaryTitle}>Daily Summary & History</h3>
              {isSaving && <p style={{ color: "#999" }}>Saving...</p>}
              <div style={styles.eventList}>
                {events.length > 0 ? (
                  events.map((event, index) => (
                    <div key={index} style={styles.eventItem}>
                      <div style={{ display: "flex", flexDirection: "column" }}>
                        <span style={{ fontWeight: "600", color: "#374651" }}>{event.type}</span>
                        <span style={{ fontSize: "14px", color: "#555", marginTop: "4px" }}>
                          {event.timestamp}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p>No events yet</p>
                )}

              </div>
            </div>
          </div>
        </div>
      ) : (
        <p>Loading...</p>
      )}
    </div>
  );
};

const styles = {
  page: {
    minHeight: "100vh",
    padding: "30px",
    display: "flex",
    justifyContent: "center",
    fontFamily: "Inter, sans-serif",
  },
  endBtn: {
    backgroundColor: "#007bff",
    color: "#fff",
    padding: "10px 15px",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
  dashboard: {
    width: "100%",
    maxWidth: "1200px",
  },
  leftCard: {
    background: "#fff",
    padding: "40px",
    borderRadius: "16px",
    boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
  },
  header: {
    fontSize: "22px",
    marginBottom: "10px",
  },
  date: {
    fontSize: "16px",
    color: "#666",
  },
  breakWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "28px",
  },
  breakLabel: {
    color: "#374651",
    fontWeight: "600",
  },
  breakTimer: {
    color: "#ff4747",
    fontWeight: "700",
    fontVariantNumeric: "tabular-nums",
  },

  clock: {
    fontSize: "38px",
    color: "#e87115",
  },
  productionWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "32px",
    margin: "15px 0 30px 0",
  },
  productionLabel: {
    color: "#374651",
    fontWeight: "600",
  },
  startBtn: {
    marginTop: "20px",
    padding: "12px 20px",
    backgroundColor: "#28a745",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    cursor: "pointer",
  },
  stopBtn: {
    marginTop: "20px",
    padding: "12px 20px",
    backgroundColor: "#dc3545",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "16px",
    cursor: "pointer",
  },
  popupOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
    backdropFilter: "blur(20px)",
  },

  popupContainer: {
    backgroundColor: "#fff",
    padding: "80px 60px",
    borderRadius: "16px",
    width: "70%",
    maxWidth: "700px",
    textAlign: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  },

  breakList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    marginBottom: "20px",
  },

  breakButton: {
    backgroundColor: "#e87115",
    color: "#fff",
    border: "none",
    padding: "10px 15px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "15px",
    transition: "0.2s",
  },
  logoutBtn: {
    backgroundColor: "#dc3545",
    color: "#fff",
    border: "none",
    padding: "10px 20px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
  },
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 999,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    padding: "25px 35px",
    textAlign: "center",
    boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
    minWidth: "300px",
  },
  buttonContainer: {
    marginTop: "20px",
    display: "flex",
    justifyContent: "center",
    gap: "10px",
  },
  historySection: {
    marginTop: "30px",
  },
  summaryTitle: {
    fontSize: "18px",
    marginBottom: "10px",
  },
  eventItem: {
    backgroundColor: "#fff7f0",
    borderRadius: "8px",
    padding: "10px 15px",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
    borderLeft: "4px solid #e87115",
    transition: "transform 0.2s ease",
  },
  eventList: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  confirmPopup: {
    backgroundColor: "#fff",
    padding: "40px 30px",
    borderRadius: "16px",
    width: "90%",
    maxWidth: "400px",
    textAlign: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
  },

  confirmBtn: {
    color: "#fff",
    border: "none",
    padding: "10px 30px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "15px",
  },
  passwordWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  toggleButton: {
    position: "absolute",
    right: "8px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "6px",
    borderRadius: "6px",
    fontSize: "18px",
    lineHeight: 1,
  },
};

export default EmployeeDashboard;